import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../config/firebase';
import { eventService, seatService } from '../services/api.service';
import SeatingMap from '../components/SeatingMap';
import SelectedSeatsInfo from '../components/SelectedSeatsInfo';
import BookingTimer from '../components/BookingTimer';
import { debounce } from 'lodash';
import './SeatingPage.css';

const SEAT_STATUS_INTERVAL = 30000; // 30 seconds
const DEBOUNCE_DELAY = 1000; // 1 second

const SeatingPage = () => {
    const { eventId, subEventId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [user] = useAuthState(auth);
    const [event, setEvent] = useState(null);
    const [seatingData, setSeatingData] = useState(null);
    const MAX_SEATS = 10; // Maximum number of seats a user can select
    const [selectedSeats, setSelectedSeats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [reservationId, setReservationId] = useState(null);
    const [reservationError, setReservationError] = useState(null);
    const [seatStatuses, setSeatStatuses] = useState({});
    const [timerStartTime, setTimerStartTime] = useState(() => {
        const stored = sessionStorage.getItem(`timer_${subEventId}`);
        return stored ? new Date(stored) : null;
    });
    const [isGuestMode, setIsGuestMode] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
    const statusCheckTimeoutRef = useRef(null);
    const isInitialNavigationMount = useRef(true);
    const cleanupRef = useRef(false);

    useEffect(() => {
        return () => {
            // Only clear timer if we're not going to checkout
            if (!window.isNavigatingToCheckout) {
                sessionStorage.removeItem(`timer_${subEventId}`);
            }
        };
    }, [subEventId]);

    const handleTimerExpired = useCallback(async () => {
        setSessionExpired(true);
        if (selectedSeats.length > 0) {
            try {
                await seatService.releaseSeats(
                    eventId,
                    subEventId,
                    selectedSeats,
                    user?.uid || null
                );
                setSelectedSeats([]);
                sessionStorage.removeItem(`timer_${subEventId}`);
                window.isNavigatingToCheckout = false;
                window.isNavigatingToSeating = false;
                navigate('/');
            } catch (error) {
                console.error('Error releasing seats:', error);
            }
        }
    }, [eventId, subEventId, selectedSeats, user, navigate]);

    // Function to check seat statuses
    const checkSeatStatuses = useCallback(async () => {
        if (!eventId || !subEventId || !seatingData) return;

        const now = Date.now();
        // Skip if last update was less than 1 second ago
        if (now - lastUpdateTime < 1000) {
            console.log('[StatusCheck] Skipping check - too soon since last update');
            return;
        }

        try {
            console.log('[StatusCheck] Fetching seat status:', {
                eventId,
                subEventId,
                timestamp: new Date().toISOString(),
                timeSinceLastUpdate: now - lastUpdateTime,
                selectedSeats: selectedSeats.map(s => typeof s === 'string' ? s : s.label)
            });

            const status = await seatService.getSeatStatus(eventId, subEventId);
            console.log('[StatusCheck] Received status from API:', status);
            
            const newStatuses = {};
            
            // Get list of selected seat labels for easy comparison
            const selectedLabels = selectedSeats.map(s => typeof s === 'string' ? s : s.label);
            
            // Initialize all seats as available first
            seatingData.seat_map.forEach(seat => {
                const labelData = JSON.parse(seat.row.label);
                const seatLabel = `${labelData.row_label}${labelData.seats[0].number}`;
                if (!seat.isBlank && seat.row.status === 'available') {
                    // If the seat is selected by us, keep it as selected
                    if (selectedLabels.includes(seatLabel)) {
                        newStatuses[seatLabel] = 'selected';
                    } else {
                        newStatuses[seatLabel] = 'available';
                    }
                }
            });

            // If we got a valid response from the API, apply those statuses
            if (Object.keys(status).length > 0) {
                Object.entries(status).forEach(([seatLabel, seatStatus]) => {
                    // Skip updating status for our selected seats
                    if (selectedLabels.includes(seatLabel)) {
                        console.log(`[StatusCheck] Keeping seat ${seatLabel} as selected (ours)`);
                        newStatuses[seatLabel] = 'selected';
                    } else {
                        const newStatus = seatStatus.status === 'reserved' ? 'unavailable' : seatStatus.status;
                        console.log(`[StatusCheck] Updating seat ${seatLabel}:`, {
                            apiStatus: seatStatus.status,
                            newStatus,
                            isSelected: selectedLabels.includes(seatLabel)
                        });
                        newStatuses[seatLabel] = newStatus;
                    }
                });

                // Update the last update time only on successful API response
                setLastUpdateTime(now);
            } else {
                // If API failed, maintain current status for non-selected seats
                Object.entries(seatStatuses).forEach(([seatLabel, currentStatus]) => {
                    if (selectedLabels.includes(seatLabel)) {
                        newStatuses[seatLabel] = 'selected';
                    } else {
                        newStatuses[seatLabel] = currentStatus;
                    }
                });
            }

            console.log('[StatusCheck] Final status update:', {
                newStatuses,
                selectedSeats: selectedLabels,
                timestamp: new Date().toISOString()
            });

            setSeatStatuses(newStatuses);
        } catch (error) {
            console.error('[StatusCheck] Failed to check seat status:', {
                error: error.message,
                stack: error.stack,
                response: error.response?.data
            });
            // On error, keep current statuses
            const newStatuses = { ...seatStatuses };
            // Ensure our selected seats stay selected
            selectedSeats.forEach(seat => {
                const seatLabel = typeof seat === 'string' ? seat : seat.label;
                newStatuses[seatLabel] = 'selected';
            });
            setSeatStatuses(newStatuses);
        }
    }, [eventId, subEventId, seatingData, selectedSeats, seatStatuses, lastUpdateTime]);

    // Effect for initial data fetch and setup
    useEffect(() => {
        const fetchEventAndSeating = async () => {
            try {
                // Get event and seating data
                const [eventResponse, seatingResponse] = await Promise.all([
                    eventService.getEventById(eventId),
                    eventService.getSeatingPlan(eventId, subEventId)
                ]);

                const subEvent = eventResponse.sub_events?.find(
                    se => se.id === subEventId || se._id === subEventId
                );

                if (!subEvent) {
                    throw new Error('Sub-event not found');
                }

                if (!subEvent.ticket_types || !subEvent.ticket_types.length) {
                    throw new Error('No ticket types found for this event. Please contact support.');
                }

                setSeatingData(seatingResponse);
                setEvent(eventResponse);
                
                // Initial status check after data is loaded
                await checkSeatStatuses();
                
                setLoading(false);
            } catch (error) {
                console.error('Error fetching event data:', error);
                setError(error.message || 'Failed to load event data');
                setLoading(false);
            }
        };

        fetchEventAndSeating();
    }, [eventId, subEventId]);

    // Effect for periodic status updates with debouncing
    useEffect(() => {
        if (!seatingData || !eventId || !subEventId) return;

        const debouncedStatusCheck = debounce(checkSeatStatuses, 1000);

        // Set up periodic status checks
        const intervalId = setInterval(debouncedStatusCheck, SEAT_STATUS_INTERVAL);

        return () => {
            clearInterval(intervalId);
            debouncedStatusCheck.cancel();
        };
    }, [seatingData, eventId, subEventId, checkSeatStatuses]);

    // Handle navigation and seat preservation
    useEffect(() => {
        if (isInitialNavigationMount.current) {
            isInitialNavigationMount.current = false;
            return;
        }

        console.log('[Navigation] Handling navigation state', {
            state: location.state,
            isNavigatingToCheckout: window.isNavigatingToCheckout,
            isNavigatingToSeating: window.isNavigatingToSeating,
            hasSelectedSeats: selectedSeats.length > 0
        });

        // Clear navigation flags when mounting
        window.isNavigatingToCheckout = false;
        window.isNavigatingToSeating = false;

        // Force a status refresh when returning from checkout
        const refreshStatuses = debounce(async () => {
            try {
                console.log('[Navigation] Forcing seat status refresh');
                await checkSeatStatuses();
            } catch (error) {
                console.error('[Navigation] Failed to refresh seat statuses:', error);
            }
        }, 1000);

        // Don't try to preserve seats when coming back from checkout
        if (location.state?.preserveSeats === false) {
            console.log('[Navigation] Not preserving seats as requested');
            setSelectedSeats([]);
            refreshStatuses();
            return;
        }

        // Only handle preserved seats if explicitly told to preserve
        if (location.state?.preserveSeats === true && location.state?.selectedSeats?.length > 0) {
            console.log('[Navigation] Found preserved seats:', location.state.selectedSeats);
            
            // Check if timer is still valid
            const timerKey = `timer_${subEventId}`;
            const timerStart = sessionStorage.getItem(timerKey);
            
            if (!timerStart) {
                console.log('[Navigation] No timer found, not preserving seats');
                setSelectedSeats([]);
                refreshStatuses();
                return;
            }

            const timeElapsed = Date.now() - new Date(timerStart).getTime();
            const timeLimit = 15 * 60 * 1000; // 15 minutes in milliseconds

            if (timeElapsed > timeLimit) {
                console.log('[Navigation] Timer expired, not preserving seats');
                sessionStorage.removeItem(timerKey);
                setSelectedSeats([]);
                refreshStatuses();
                return;
            }

            // Timer is still valid, preserve the seats
            setSelectedSeats(location.state.selectedSeats);
            refreshStatuses();
        } else {
            // If we're mounting without any preservation intent, refresh statuses
            refreshStatuses();
        }

        return () => {
            refreshStatuses.cancel();
        };
    }, [location.state, subEventId, checkSeatStatuses, selectedSeats.length]);

    // Handle cleanup
    useEffect(() => {
        return () => {
            // Prevent multiple cleanup calls
            if (cleanupRef.current) {
                console.log('[Cleanup] Cleanup already performed, skipping');
                return;
            }

            console.log('[Cleanup] Component cleanup triggered', {
                isNavigatingToCheckout: window.isNavigatingToCheckout,
                preserveSeats: location.state?.preserveSeats,
                selectedSeatsCount: selectedSeats.length,
                hasCleanedUp: cleanupRef.current
            });

            // Only release seats if:
            // 1. We're not navigating to checkout
            // 2. We have selected seats
            // 3. We have valid IDs and session
            // 4. We haven't already cleaned up
            if (!window.isNavigatingToCheckout && 
                selectedSeats.length > 0 && 
                eventId && 
                subEventId &&
                !cleanupRef.current) {
                
                const sessionId = localStorage.getItem('sessionId');
                if (!sessionId) {
                    console.warn('[Cleanup] No session ID found for cleanup');
                    return;
                }

                console.log('[Cleanup] Releasing seats', {
                    selectedSeats,
                    eventId,
                    subEventId,
                    sessionId
                });

                cleanupRef.current = true;

                seatService.releaseSeats(
                    eventId,
                    subEventId,
                    selectedSeats.map(seat => typeof seat === 'string' ? seat : seat.label),
                    sessionId
                ).then(() => {
                    console.log('[Cleanup] Successfully released seats');
                    sessionStorage.removeItem(`timer_${subEventId}`);
                }).catch(error => {
                    console.error('[Cleanup] Failed to release seats:', error);
                    cleanupRef.current = false;
                });
            } else {
                console.log('[Cleanup] Skipping seat release', {
                    isNavigatingToCheckout: window.isNavigatingToCheckout,
                    isNavigatingToSeating: window.isNavigatingToSeating,
                    preserveSeats: location.state?.preserveSeats,
                    selectedSeatsCount: selectedSeats.length,
                    hasCleanedUp: cleanupRef.current
                });
            }
        };
    }, [selectedSeats, eventId, subEventId, location.state]);

    const handleSeatClick = async (seatLabel) => {
        const isSelected = selectedSeats.some(s => 
            typeof s === 'string' ? s === seatLabel : s.label === seatLabel
        );
        
        console.log('[SeatClick] Processing seat click', {
            seatLabel,
            isSelected,
            currentSelectedSeats: selectedSeats,
            seatingData
        });

        try {
            if (isSelected) {
                console.log('[SeatClick] Releasing selected seat', seatLabel);
                const sessionId = localStorage.getItem('sessionId');
                // Release the seat in the backend
                await seatService.releaseSeats(
                    eventId, 
                    subEventId, 
                    [seatLabel],
                    sessionId
                );
                
                // Remove from selected seats
                setSelectedSeats(prev => prev.filter(s => 
                    typeof s === 'string' ? s !== seatLabel : s.label !== seatLabel
                ));
                
                // Mark as available immediately
                setSeatStatuses(prev => ({
                    ...prev,
                    [seatLabel]: 'available'
                }));

                // If no seats are selected, clear the timer
                if (selectedSeats.length === 1) {
                    setTimerStartTime(null);
                    sessionStorage.removeItem(`timer_${subEventId}`);
                }
            } else {
                // Check if we can add more seats
                if (selectedSeats.length >= MAX_SEATS) {
                    setReservationError(`You can select up to ${MAX_SEATS} seats.`);
                    return;
                }

                // Get the ticket type for this seat
                const defaultTicketType = event.sub_events.find(se => 
                    se.id === subEventId || se._id === subEventId
                )?.ticket_types[0];

                if (!defaultTicketType) {
                    throw new Error('No ticket type found for this event');
                }

                // Find the seat in seatingData
                const seatInfo = seatingData.seat_map.find(seat => {
                    try {
                        const labelData = JSON.parse(seat.row.label);
                        const seatNumber = labelData.seats[0].number;
                        const fullLabel = `${labelData.row_label}${seatNumber}`;
                        return fullLabel === seatLabel;
                    } catch (e) {
                        console.error('[SeatClick] Error parsing seat label:', e);
                        return false;
                    }
                });

                if (!seatInfo) {
                    console.error('[SeatClick] Seat info not found for label:', seatLabel);
                    throw new Error('Koltuk bilgisi bulunamadı');
                }

                console.log('[SeatClick] Found seat info:', {
                    seatInfo,
                    defaultTicketType,
                    seatLabel,
                    seatingData
                });

                // Get price from the ticket type
                const price = defaultTicketType.price || 0;

                // Get category from the seat info
                const category = seatInfo.category || seatInfo.price_category;
                if (!category) {
                    console.error('[SeatClick] No category found for seat:', {
                        seatInfo,
                        seatLabel
                    });
                    throw new Error('Koltuk kategorisi bulunamadı');
                }

                // Add the new seat with ticket type information
                const newSeat = {
                    label: seatLabel,
                    price: price,
                    category: category,
                    ticket_type_id: defaultTicketType.id || defaultTicketType._id
                };

                console.log('[SeatClick] Adding new seat:', newSeat);

                setSelectedSeats(prev => [...prev, newSeat]);
                
                // Mark as selected immediately
                setSeatStatuses(prev => ({
                    ...prev,
                    [seatLabel]: 'selected'
                }));

                // Start timer if this is the first seat
                if (selectedSeats.length === 0) {
                    const now = new Date();
                    setTimerStartTime(now);
                    sessionStorage.setItem(`timer_${subEventId}`, now.toISOString());
                }
            }
        } catch (error) {
            console.error('[SeatClick] Failed to handle seat action:', error);
            setReservationError('Koltuk seçimi yapılamadı. Lütfen tekrar deneyiniz.');
            setSeatStatuses(prev => ({
                ...prev,
                [seatLabel]: 'unavailable'
            }));
        }
    };

    const handleCheckout = async () => {
        console.log('[Navigation] Starting checkout process', {
            user,
            isGuestMode,
            selectedSeatsCount: selectedSeats.length
        });

        // Show login prompt if user is not logged in and not in guest mode
        if (!user && !isGuestMode) {
            setShowLoginPrompt(true);
            return;
        }

        try {
            // Calculate total amount from selected seats
            const totalAmount = selectedSeats.reduce((sum, seat) => {
                const price = typeof seat === 'string' 
                    ? seatingData.price_categories[seat.price_category]?.price || 0
                    : seat.price || 0;
                return sum + price;
            }, 0);

            console.log('[Navigation] Navigating to checkout', {
                selectedSeats,
                totalAmount,
                reservationId
            });

            window.isNavigatingToCheckout = true;
            window.isNavigatingToSeating = false;
            navigate('/checkout', { 
                state: { 
                    selectedSeats,
                    event,
                    totalAmount,
                    reservationId 
                } 
            });
        } catch (error) {
            console.error('[Navigation] Checkout navigation failed:', error);
            window.isNavigatingToCheckout = false;
            setError('Failed to proceed to checkout. Please try again.');
        }
    };

    if (loading) return (
        <div className="loading-container">
            <div className="loading-spinner"></div>
        </div>
    );
    if (error) return (
        <div className="error-container">
            <h2>Hata</h2>
            <p>{error}</p>
            <div className="error-details">
                <p>Lütfen daha sonra tekrar deneyiniz veya destek ekibi ile iletişime geçiniz.</p>
            </div>
            <button onClick={() => navigate('/')}>Ana Sayfaya Dön</button>
        </div>
    );
    if (!event || !seatingData) return (
        <div className="error-container">
            <p>Etkinlik bilgileri bulunamadı</p>
            <button onClick={() => navigate('/')}>Ana Sayfaya Dön</button>
        </div>
    );

    // Get the current sub-event details
    const currentSubEvent = event.sub_events?.find(se => se.id === subEventId || se._id === subEventId);
    if (!currentSubEvent) return (
        <div className="error-container">
            <p>Gösterim bilgileri bulunamadı</p>
            <button onClick={() => navigate('/')}>Ana Sayfaya Dön</button>
        </div>
    );

    // Format date and time
    const eventDate = new Date(currentSubEvent.date);
    const formattedDate = eventDate.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
    const formattedTime = eventDate.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit'
    });

    return (
        <div className="seating-page">
            <div className="event-details-banner">
                <div className="event-image">
                    <img src={event.image_url} alt={event.title} />
                </div>
                <div className="event-info">
                    <h1>{event.title}</h1>
                    <div className="event-meta">
                        <p><i className="far fa-calendar"></i> {formattedDate}</p>
                        <p><i className="far fa-clock"></i> {formattedTime}</p>
                        <p><i className="fas fa-map-marker-alt"></i> {currentSubEvent.location?.venue || event.location?.venue}</p>
                        <p><i className="fas fa-city"></i> {currentSubEvent.location?.city || event.location?.city}</p>
                    </div>
                </div>
            </div>

            <div className="main-content">
                <div className="seating-container">
                    {!sessionExpired && <BookingTimer onTimeout={handleTimerExpired} startTime={timerStartTime} />}
                    <div className="stage-container">
                        <div className="stage">SAHNE</div>
                    </div>
                    <SeatingMap 
                        selectedSeats={selectedSeats}
                        onSeatSelect={handleSeatClick}
                        seatingData={seatingData}
                        seatStatuses={seatStatuses}
                    />
                    {selectedSeats.length > 0 && (
                        <SelectedSeatsInfo
                            selectedSeats={selectedSeats}
                            seatingData={seatingData}
                            event={event}
                            reservationId={reservationId}
                        />
                    )}
                </div>
            </div>

            {showLoginPrompt && (
                <div className="login-prompt-overlay">
                    <div className="login-prompt-modal">
                        <h3>Giriş Yapmak İster misiniz?</h3>
                        <p>Hesabınıza giriş yapabilir veya misafir olarak devam edebilirsiniz.</p>
                        <div className="login-prompt-buttons">
                            <button onClick={() => navigate('/login')} className="login-button">
                                Giriş Yap
                            </button>
                            <button onClick={() => {
                                setIsGuestMode(true);
                                setShowLoginPrompt(false);
                                handleCheckout();
                            }} className="guest-button">
                                Misafir Olarak Devam Et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {reservationError && (
                <div className="error-message">
                    {reservationError}
                </div>
            )}
        </div>
    );
};

export default SeatingPage;
